import { Trans } from "@lingui/react/macro";
import React from "react";
import * as sharedStyles from "./CruiseInfoSummary2.module.scss";
import * as styles from "./SpecialVoyageInfoSummary.module.scss";
import {
  Button,
  PartialSanityImageType,
  PromoBadges,
  SectionContainer
} from "@silversea-ssc/ui-components-react";
import CruiseRecapColumns from "./CruiseRecap/CruiseRecapColumns";

import { dequal } from "dequal/lite";
import { ECommerceStatus } from "src/ssc-api/useECommerceStatus";
import {
  ActivePromoForCruise,
  activePromosToPromoBadges
} from "src/utils/promos";
import NativeHeroImage from "components/NativeHeroImage/NativeHeroImage";
import clsx from "clsx";
import PriceRecap from "./PriceRecap";
import ECommerceButtons from "components/ECommerceButtons";
import { CruisePriceAndOffersV2Response } from "src/ssc-api/prices/useCruisePrice";
import { ECommerceBenefitsContentFragment } from "types/graphql-types";
import MaybeECommerceBenefits from "components/ECommerceBenefits/MaybeECommerceBenefits";

interface SpecialVoyageInfoSummaryProps {
  heroImage?: PartialSanityImageType;
  cruiseCode: string;
  cruiseName: string;
  cruiseMarketingName?: string;
  prices?: CruisePriceAndOffersV2Response;
  indexPage?: {
    url?: string;
    title?: JSX.Element | string;
  };
  shipName?: string;
  departureDate?: string;
  arrivalDate?: string;
  duration?: number;
  raqUrl?: string;
  ecommerceStatus: ECommerceStatus;
  activePromos?: ActivePromoForCruise[];
  eCommerceBenefitsContent: ECommerceBenefitsContentFragment | undefined;
  cruiseTags: string[];
}

const SpecialVoyageInfoSummary: React.FC<
  React.PropsWithChildren<SpecialVoyageInfoSummaryProps>
> = ({
  cruiseCode,
  cruiseName,
  cruiseMarketingName,
  prices,
  indexPage,
  shipName,
  arrivalDate,
  departureDate,
  duration,
  raqUrl,
  ecommerceStatus,
  children,
  activePromos = [],
  heroImage,
  eCommerceBenefitsContent,
  cruiseTags
}) => {
  const comboShipBoardCredit = prices?.tokens?.["combo_ship_board_credit"];
  const promoBadges = activePromosToPromoBadges(activePromos);

  return (
    <div className={styles.wrapper}>
      {heroImage && (
        <NativeHeroImage
          image={heroImage}
          alt={cruiseName}
          className={styles.heroImage}
        />
      )}

      <SectionContainer
        backgroundColor="transparent"
        spacingBottom={null}
        dataContentName="cruise_summary"
      >
        <div>
          <div
            className={sharedStyles.specialVoyageRoot}
            data-content-name="cruise_info_summary"
          >
            <div className={sharedStyles.backTo}>
              <Button
                variant="inline-text"
                renderAs="a"
                href={indexPage?.url}
                dataContentPiece="view_all_cruises_topleft"
              >
                {indexPage?.title}
              </Button>
            </div>

            <div className={sharedStyles.titleInfo}>
              {cruiseMarketingName && (
                <div className={styles.cruiseMarketingName}>
                  {cruiseMarketingName}
                </div>
              )}
              <h1 className={sharedStyles.cruiseName}>{cruiseName}</h1>
            </div>

            <CruiseRecapColumns
              className={clsx(sharedStyles.cruiseRecap, styles.cruiseRecap)}
              shipName={shipName}
              arrivalDate={arrivalDate}
              departureDate={departureDate}
              duration={duration}
              cruiseTags={cruiseTags}
              layout="auto-wide"
            />

            <div
              className={clsx(
                sharedStyles.promoBadgesContainer,
                styles.promoBadgesContainer
              )}
            >
              {promoBadges.length > 0 && (
                <React.Fragment>
                  <div className={sharedStyles.promoBadgesTitle}>
                    <Trans>Featured offers</Trans>
                  </div>
                  <PromoBadges
                    promos={promoBadges}
                    className={sharedStyles.promoBadges}
                  />
                </React.Fragment>
              )}
            </div>

            <PriceRecap
              className={clsx(styles.priceRecap, sharedStyles.priceRecap)}
              comboShipBoardCredit={comboShipBoardCredit}
              prices={prices}
              departureDate={departureDate}
              cruiseCode={cruiseCode}
            />

            <div
              className={clsx(sharedStyles.ctaContainers, styles.ctaContainers)}
            >
              <div className={styles.ctaButtons}>
                <ECommerceButtons
                  url={ecommerceStatus}
                  size="regular"
                  raqUrl={raqUrl}
                />
              </div>

              <MaybeECommerceBenefits
                {...eCommerceBenefitsContent}
                prices={prices}
                cruiseCode={cruiseCode}
                ecommerceStatus={ecommerceStatus}
              />
            </div>
          </div>
        </div>
        {children}
      </SectionContainer>
    </div>
  );
};

export default React.memo(SpecialVoyageInfoSummary, dequal);
